<template>
    <div class="flex flex-column justify-content-start gap-4 ai-summary-container">
        <div class="flex flex-column gap-1 border-bottom-1 pb-3 borderColor">
            <span class="summary-title">{{ summaryData.studentSummary.title || 'Class Summary' }}</span>
            <div class="flex gap-3 text-sm text-500">
                <span><i class="pi pi-calendar mr-1"></i>{{ formatToDisplayDate(classInstance.date) }}</span>
                <span><i class="pi pi-clock mr-1"></i>{{ formatToDisplayDuration(classInstance.start, classInstance.end)
                    }}</span>
            </div>
        </div>

        <section class="summary-section bg-gray-50 p-3 border-round" v-if="role === 'tutor'">
            <h3 class="section-header text-700">Tutor's Private Notes </h3>
            <div class="grid text-sm">
                <div class="col-12">
                    <span class="font-semibold">Overview:</span>
                    <p class="Paragraph-Medium mb-3">{{ summaryData.teacherSummary.overview }}</p>
                </div>
                <div class="col-12 md:col-6">
                    <span class="font-semibold">Student Performance:</span>
                    <p v-for="perf in summaryData.teacherSummary.studentPerformance" :key="perf.studentId">
                        {{ perf.notes }}
                    </p>
                </div>
                <div class="col-12 md:col-6">
                    <span class="font-semibold">Next Lesson Plan:</span>
                    <ul class="summary-list">
                        <li v-for="plan in summaryData.teacherSummary.nextClassPlan" :key="plan">{{ plan }}</li>
                    </ul>
                </div>
                <div class="col-12 md:col-6">
                    <span class="font-semibold">Action Items:</span>
                    <ul class="summary-list">
                        <li v-for="action in summaryData.teacherSummary.actionItems" :key="action">{{ action }}</li>
                    </ul>
                </div>
            </div>
        </section>

        <section class="summary-section">
            <h3 class="section-header" :style="{ color: role === 'tutor' ? '#005F5F' : '#A72E44' }">
                Student Overview
            </h3>
            <p class="Paragraph-Medium mb-3">{{ summaryData.studentSummary.overview }}</p>

            <div v-if="summaryData.studentSummary.keyPoints?.length" class="mb-3">
                <span class="font-bold block mb-2">Key Takeaways:</span>
                <ul class="summary-list">
                    <li v-for="(point, i) in summaryData.studentSummary.keyPoints" :key="i">{{ point }}</li>
                </ul>
            </div>

            <div v-if="summaryData.studentSummary.definitions?.length" class="mb-3">
                <span class="font-bold block mb-2">Vocabulary & Definitions:</span>
                <div class="grid">
                    <div v-for="(def, i) in summaryData.studentSummary.definitions" :key="i" class="col-12 md:col-6">
                        <div class="def-card">
                            <span class="font-semibold text-orange-600">{{ def.term }}:</span> {{ def.meaning }}
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="summaryData.studentSummary.homework?.length" class="homework-box p-3 border-round">
                <span class="font-bold block mb-1"><i class="pi pi-pencil mr-2"></i>Homework:</span>
                <ul class="summary-list mb-0">
                    <li v-for="(item, i) in summaryData.studentSummary.homework" :key="i">{{ item }}</li>
                </ul>
            </div>
        </section>

        <div class="flex justify-content-between align-items-center text-xs text-400 mt-2">
            <span>Generated by ILearn'd AI Assistant</span>
            <span>Ref: {{ classInstance.status }}</span>
        </div>
    </div>
</template>

<script setup>
import { defineProps, computed } from "vue"; // Added computed
import { formatToDisplayDate } from "@/utils/cleanups";
import { useAuthStore } from "@/stores/authStore";

const props = defineProps({
    data: {
        type: Object,
        required: true
    }
});

const authStore = useAuthStore();
const role = computed(() => authStore.user?.role || "");

const summaryData = computed(() => props.data.summary);
const classInstance = computed(() => props.data.classInstance);


const formatToDisplayDuration = (start, end) => {
    if (!start || !end) return '';
    const s = new Date(start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const e = new Date(end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${s} - ${e}`;
};
</script>

<style scoped>
.ai-summary-container {
    max-width: 800px;
}

.summary-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2c3e50;
}

.section-header {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    border-left: 4px solid currentColor;
    padding-left: 0.75rem;
}

.summary-list {
    padding-left: 1.2rem;
    margin-top: 0.5rem;
}

.summary-list li {
    margin-bottom: 0.4rem;
}

.def-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    padding: 0.75rem;
    border-radius: 6px;
    height: 100%;
}

.homework-box {
    background-color: #fff9db;
    border: 1px dashed #fab005;
}

.topic-tag {
    background: #e2e8f0;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 0.8rem;
}

.borderColor {
    border-color: #eee;
}
</style>